/**
 * Personal Mode Limits for Data Export
 *
 * Defines the row count limits for Personal mode exports and provides
 * utility functions for checking if exports exceed these limits.
 */

/**
 * Maximum row count for exports in Personal mode.
 * Exports larger than this require Pro mode.
 */
export const PERSONAL_MODE_MAX_EXPORT_ROWS = 100_000;

/**
 * Check result for Personal mode export limits
 */
export type ExportLimitCheck = {
  /** Whether the export exceeds Personal mode limits */
  exceedsLimit: boolean;
  /** Total row count to be exported */
  rowCount: number;
  /** Human-readable description of the limit status */
  description: string;
};

/**
 * Checks if an export exceeds Personal mode limits.
 *
 * In Personal mode, exports are limited to 100,000 rows.
 * Larger exports require Pro mode.
 *
 * @param rowCount - Total row count to be exported
 * @returns Limit check result
 */
export function checkExportLimit(rowCount: number): ExportLimitCheck {
  const exceedsLimit = rowCount > PERSONAL_MODE_MAX_EXPORT_ROWS;

  const description = exceedsLimit
    ? `Export exceeds the ${PERSONAL_MODE_MAX_EXPORT_ROWS.toLocaleString()} row limit for Personal mode (${rowCount.toLocaleString()} rows).`
    : `Export is within Personal mode limits (${rowCount.toLocaleString()} rows).`;

  return {
    exceedsLimit,
    rowCount,
    description,
  };
}

/**
 * Formats the export row count limits for display in the UI.
 *
 * @returns Human-readable string describing the Personal mode limit
 */
export function getExportLimitString(): string {
  return `${PERSONAL_MODE_MAX_EXPORT_ROWS.toLocaleString()} rows`;
}

/**
 * Calculates the total row count from query result sets.
 *
 * @param resultSets - Array of result sets with rowCount or rows
 * @returns Total row count across all result sets
 */
export function calculateTotalExportRows(
  resultSets: Array<{ rowCount?: number; rows?: unknown[] }>
): number {
  return resultSets.reduce((total, rs) => {
    const count = rs.rowCount ?? rs.rows?.length ?? 0;
    return total + count;
  }, 0);
}
